import AxiosService from './AxiosService';
import { AuthenticationServiceError } from './errors';
import * as auth from 'utility/Authentication';
import { TIMEZONE_OFFSET_HEADER } from './constants';

import { snakeToCamelCaseKeys } from './helpers';

import {
  changeLanguageInStorage,
  PSEUDO_LANGUAGE,
  getCurrentLanguageFromStorage,
} from 'i18n-config';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

class AuthenticationService {
  /*
   * Singleton ES6 pattern
   * https://www.sitepoint.com/javascript-design-patterns-singleton/
   */
  constructor() {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = this;
    }
    return AuthenticationService.instance;
  }

  endpoint = '/auth';

  formatError = (error = {}) => {
    const { response } = error;
    if (!response || response.status === 404) {
      return new Error(
        'Something went wrong. Check your network connection and try again.'
      );
    }

    return new Error(
      `${response.statusText} -- ${response.data ? response.data.message : ''}`
    );
  };

  classifyError = (error = {}, source = null) => {
    // Something very odd happened; punt
    if (!(error instanceof Error)) {
      return new Error(
        'Something went wrong. Check your network connection and try again.'
      );
    }

    if (!error.isAxiosError) {
      throw error;
    }

    const { response } = error;
    // non-recoverable catch-call e.g. exceptions, Mixed content / network errors
    if (!response || response.status >= 500) {
      throw error;
    }

    throw new AuthenticationServiceError(error.message, {
      status: response.status,
      payload: response.data,
      source,
    });
  };

  async fetchAccess() {
    try {
      const { data } = await AxiosService.get(`${this.endpoint}/access`);
      return data;
    } catch (error) {
      let err = error;
      if (error.isAxiosError) {
        err = this.formatError(error);
      }
      throw err;
    }
  }

  async bootstrapSession() {
    try {
      let user = null;
      let business = null;
      let access = null;
      let teamMember = null;
      let filterMetaData = null;
      let externalApps = {};
      let pluginApps = [];
      const authorised = auth.status.authorized;
      if (authorised) {
        const [bootstrapResponse, filtersResponse] = await Promise.all([
          AxiosService.get('/auth/bootstrap'),
          AxiosService.get('/filters/metadata'),
        ]);
        ({
          data: {
            me: user,
            business,
            access,
            team: teamMember,
            external_apps: externalApps,
            enabled_plugin_apps: pluginApps,
          },
        } = bootstrapResponse);
        ({ data: filterMetaData } = filtersResponse);
      }

      // eek should not realy be a service level thing

      if (teamMember?.language) {
        changeLanguageInStorage(
          getCurrentLanguageFromStorage() === PSEUDO_LANGUAGE
            ? PSEUDO_LANGUAGE
            : teamMember.language
        );
      }
      return {
        user,
        business,
        access,
        teamMember,
        filterMetaData,
        externalApps,
        pluginApps,
      };
    } catch (error) {
      let err = error;
      if (error.isAxiosError) {
        const currentError = this.formatError(error);
        err =
          typeof currentError === 'string'
            ? currentError
            : currentError.toString();
      }
      throw err;
    }
  }

  async login(username, password) {
    let user = {};
    try {
      const response = await AxiosService.post(
        `/login`,
        {
          username,
          password,
        },
        {
          timeout: null,
          headers: {
            [TIMEZONE_OFFSET_HEADER]:
              Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }
      );

      const { data, status } = response;

      // MFA auth_challenge_method returned
      if (status === 202 && data.auth_challenge_method) {
        const { auth_challenge_method } = data;
        return { token: null, user: null, auth_challenge_method };
      }

      user = data;

      // Only single-business users will receive a complete-scoped token and cookie
      // multi-business users and superusers will later have to select a business
      const firstBusinessId = user?.first_business?.id;
      if (firstBusinessId) {
        auth.status.businessId = firstBusinessId;
      }

      return { token: null, user, auth_challenge_method: null };
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async validateMfaToken(mfaOption, mfaToken, mfaRemember) {
    let user = {};
    try {
      const response = await AxiosService.post(
        `/auth/validate-mfa-token`,
        {
          type: mfaOption,
          token: mfaToken,
          remember: mfaRemember,
        },
        {
          timeout: null,
        }
      );

      const { data } = response;

      user = data;

      // Only single-business users will receive a complete-scoped token and cookie
      // multi-business users and superusers will later have to select a business
      const firstBusinessId = user?.first_business?.id;
      if (firstBusinessId) {
        auth.status.businessId = firstBusinessId;
      }

      return { token: null, user, auth_challenge_method: null };
    } catch (error) {
      throw this.classifyError(error, 'token');
    }
  }

  async initiateTOTPSetup() {
    const response = await AxiosService.post(`/mfa/totp/setup`);
    return response.data;
  }

  async getTOTPSetupStatus() {
    const response = await AxiosService.get(`/mfa/totp/status`);
    return response.data;
  }

  async verifyTOTPSetup(token) {
    const response = await AxiosService.post(`/mfa/totp/verify`, { token });
    return response.data;
  }

  async deleteTOTPSetup() {
    const response = await AxiosService.delete(`/mfa/totp/disable`);
    return response.data;
  }

  async getOTPTokenViaEmail() {
    const response = await AxiosService.post(`/mfa/email/send`);
    return response.data;
  }

  async logout() {
    try {
      await AxiosService.post(`/logout`);
    } catch (error) {
      // we don't need to report 401's to sentry
      if (!(error?.status === 401 || error?.response?.status === 401)) {
        monitoringExceptionHelper(error);
      }
      let err = error;
      if (error.isAxiosError) {
        err = this.formatError(error);
      }
      throw err;
    }
  }

  async fetchBusinesses(showAllBusinesses) {
    try {
      let url = '/my-businesses';
      // This doesn't do anything unless the current user is a super user:
      if (showAllBusinesses) {
        url += '?show_all=true';
      }
      const { data } = await AxiosService.get(url); // under team
      return snakeToCamelCaseKeys(data.businesses);
    } catch (error) {
      monitoringExceptionHelper(error);
      let err = error;
      if (error.isAxiosError) {
        err = this.formatError(error);
      }
      throw err;
    }
  }

  async fetchBusiness(businessId) {
    try {
      const response = await AxiosService.get(
        `/business/${businessId}?v2=true`
      );
      return response.data;
    } catch (error) {
      monitoringExceptionHelper(error);
      let err = error;
      if (error.isAxiosError) {
        err = this.formatError(error);
      }
      throw err;
    }
  }

  async chooseBusiness(businessId) {
    try {
      const response = await AxiosService.post(`select-business`, {
        businessId,
      });
      return response.data;
    } catch (error) {
      monitoringExceptionHelper(error);
      let err = error;
      if (error.isAxiosError) {
        err = this.formatError(error);
      }
      throw err;
    }
  }

  async requestPasswordReset(email) {
    try {
      const response = await AxiosService.post(`/request-password-reset`, {
        email,
      });
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async resetPassword(user_token, password) {
    try {
      const response = await AxiosService.post(`/reset-password`, {
        password,
        user_token,
      });
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async verifyaccount(user_token, password, firstName, lastName, phone) {
    try {
      const response = await AxiosService.post(`/verify-account`, {
        password,
        user_token,
        first_name: firstName,
        last_name: lastName,
        ...(phone ? { phone } : null),
      });
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async fetchCurrentUserUsingToken(token) {
    try {
      const response = await AxiosService.post(`/me`, {
        token,
      });
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async getBusinessInformation() {
    try {
      const response = await AxiosService.get('/business/mine?v2=true');
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async updateBusinessInformation(data) {
    try {
      const response = await AxiosService.patch('/business/mine?v2=true', data);
      return response.data;
    } catch (error) {
      throw this.classifyError(error);
    }
  }

  async createUser(user) {
    const response = await AxiosService.post(`/register`, user);
    return response.data;
  }

  async createBusiness(businessInfo, { no_cc }) {
    const response = await AxiosService.post(
      '/business?v2=true',
      businessInfo,
      {
        params: { no_cc }, // send no_cc param so the trial can be activated and billing page avoided
      }
    );
    return response.data;
  }

  async updateBusiness(businessInfo) {
    const response = await AxiosService.patch(
      '/business?v2=true',
      businessInfo
    );
    return response.data;
  }

  async getBusinessTextNumber() {
    const response = await AxiosService.get('/business/sms-account');

    return response.data;
  }

  async preLogin({ username }) {
    const { data } = await AxiosService.post('/auth/pre-login', {
      username,
    });
    return snakeToCamelCaseKeys(data);
  }

  async getBusinessCalendarSettings(options) {
    const { data } = await AxiosService.get('/business/calendar', options);
    return data;
  }

  async updateBusinessCalendarSettings(payload, options) {
    const { data } = await AxiosService.post(
      '/business/calendar',
      payload,
      options
    );
    return data;
  }

  async getBusinessHolidaysByCountry(body, options) {
    const { data } = await AxiosService.post(
      '/business/calendar/holidays',
      body,
      options
    );
    return data;
  }

  async getBusinessHolidaysCountries(options) {
    const { data } = await AxiosService.get(
      '/business/calendar/countries',
      options
    );
    return data;
  }
}

const instance = new AuthenticationService();
Object.freeze(instance);

export default instance;
